import {
  GroupLevelAttribute,
  GroupLevelAttributeValue
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { CourseStatus } from '@/jbi-shared/types/jtms.types';
import { RootState } from '@/store/store';
import { GetterTree } from 'vuex';
import { CourseState } from './course.types';

export const courseGetters: GetterTree<CourseState, RootState> = {
  // Value from BASE course details (used explicitly in router guard)
  groupRegistrationEnabled: (state: CourseState) =>
    state.courseBySlug?.groupRegistration === true,

  courseExists: (state: CourseState) => !!state.courseBySlug,

  isCourseCompleted: (state: CourseState) =>
    state.courseBySlug?.status === CourseStatus.COMPLETED,

  isCourseInProgress: (state: CourseState) =>
    state.courseBySlug?.status === CourseStatus.IN_PROGRESS,

  isCoursePublished: (state: CourseState) =>
    state.courseBySlug?.status === CourseStatus.PUBLISHED,

  // Value from FULL course details, use in standard UI component logic
  isGroupRegistrationEnabled(state): boolean {
    return state.publicCourseState.courseDetails?.groupRegistration ?? false;
  },

  // Value from FULL course details, use in standard UI component logic
  // Prevent direct mutation
  isFreeCourse(state): boolean {
    return state.publicCourseState.courseDetails?.isFreeCourse ?? false;
  },

  // Value from FULL course details, use in standard UI component logic
  // Prevent direct mutation
  hasQuestionnaire(state): boolean {
    return state.publicCourseState.courseDetails?.hasQuestionnaire ?? false;
  },

  hasMultipleCoursePackages(state): boolean {
    const publicCourseDetails = state.publicCourseState.courseDetails;
    if (!publicCourseDetails) {
      return false;
    }

    if (
      !publicCourseDetails.coursePackages ||
      !Array.isArray(publicCourseDetails.coursePackages)
    ) {
      return false;
    }

    return publicCourseDetails.coursePackages.length > 1;
  },

  hasCourseModules(state): boolean {
    const publicCourseDetails = state.publicCourseState.courseDetails;

    if (!publicCourseDetails) {
      return false;
    }

    if (
      !publicCourseDetails.courseModules ||
      !Array.isArray(publicCourseDetails.courseModules)
    ) {
      return false;
    }

    return publicCourseDetails.courseModules.length > 0;
  },

  // NOTE: There are no UI designs that address displaying multiple currencies
  // from different course packages. This should be fleshed out for consistency sake.
  // Defaults to the currency defined in the first course package for now (assumed that
  // all course packages will have the same currency)
  currency(state): string {
    const publicCourseDetails = state.publicCourseState.courseDetails;

    if (
      !publicCourseDetails ||
      (publicCourseDetails.coursePackages &&
        Array.isArray(publicCourseDetails.coursePackages) &&
        publicCourseDetails.coursePackages.length === 0)
    ) {
      return 'AUD';
    }

    return publicCourseDetails.coursePackages![0].currency!;
  },

  hasMissingRequiredCourseLevelAttributes(state, getters, rootState): boolean {
    const requiredGLAs: GroupLevelAttribute[] =
      state.requiredPublicCourseLevelAttributes;
    const allCourseGLAs: GroupLevelAttributeValue[] | undefined =
      rootState.admin.groupLevelAttributeValuesByGroupId;

    // Early returns on guard clauses
    if (!allCourseGLAs || !requiredGLAs || requiredGLAs.length === 0) {
      return false;
    }

    // Returns TRUE if any required item fails the validation
    return requiredGLAs.some((req) => {
      // Find the matching attribute in the values list by ID
      const matchingGLA = allCourseGLAs.find(
        (gla) => Number(gla.id) === Number(req.id)
      );

      // A. Attribute is completely missing from the values list
      if (!matchingGLA) {
        return true;
      }

      // B. Attribute exists, but value is empty
      const val = matchingGLA.value;

      if (val === null || val === undefined) {
        return true;
      }

      if (typeof val === 'string') {
        return val.trim() === '';
      }

      if (Array.isArray(val)) {
        return val.length === 0;
      }

      // If all checks passed, assumed false
      return false;
    });
  },

  missingRequiredCourseLevelAttributes(state, getters, rootState): string[] {
    const requiredGLAs: GroupLevelAttribute[] =
      state.requiredPublicCourseLevelAttributes;
    const allCourseGLAs: GroupLevelAttributeValue[] | undefined =
      rootState.admin.groupLevelAttributeValuesByGroupId;

    // Early returns on guard clauses
    if (!allCourseGLAs || !requiredGLAs || requiredGLAs.length === 0) {
      return [];
    }

    // Filter down to only the missing/empty attributes
    return (
      requiredGLAs
        .filter((req) => {
          // Find the matching attribute value by ID
          const matchingGLA = allCourseGLAs.find(
            (gla) => Number(gla.id) === Number(req.id)
          );

          // A. Attribute is completely missing from the values list
          if (!matchingGLA) {
            return true;
          }

          // B. Attribute exists, but value is empty
          const val = matchingGLA.value;

          if (val === null || val === undefined) {
            return true;
          }

          if (typeof val === 'string') {
            return val.trim() === '';
          }

          if (Array.isArray(val)) {
            return val.length === 0;
          }

          // If all checks passed, assumed false
          return false;
        })
        // Extract the labels
        .map((req) => req.label)
    );
  }
};
